import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DocumotoManualNodeDto } from '../../api/documoto/types';
import { isDocumotoChapterNode } from './documotoNodeKind';

type Props = {
	rootNodes: DocumotoManualNodeDto[];
	returnSerial: string;
};

function ChapterCard({ item, depth, returnSerial }: { item: DocumotoManualNodeDto; depth: number; returnSerial: string }) {
	const [open, setOpen] = useState(false);
	const children = item.children ?? [];

	return (
		<div className={'toc-chapter-slot' + (open ? ' toc-chapter-slot--open' : '')}>
			<button type="button" className={'toc-card toc-card--chapter' + (open ? ' toc-card--chapter-open' : '')} onClick={() => setOpen(!open)} aria-expanded={open}>
				<div className="toc-card-visual">
					{item.rawImage ? <img src={item.rawImage} alt="" /> : null}
				</div>
				<div className="toc-card-meta">
					<div className="toc-card-name">{item.name}</div>
					<div className="toc-card-id">Chapter {String(item.entityId)}</div>
				</div>
				<span className="toc-card-chevron" aria-hidden>
					{open ? '▲' : '▼'}
				</span>
			</button>
			{open ? (
				<div className="toc-cards-expand" role="region" aria-label={`Contents of ${item.name}`} style={{ paddingLeft: `${10 + depth * 14}px` }}>
					{children.length === 0 ? (
						<p className="toc-muted toc-cards-expand-status">No items in this chapter.</p>
					) : (
						<TocCardBand nodes={children} depth={depth + 1} returnSerial={returnSerial} />
					)}
				</div>
			) : null}
		</div>
	);
}

function PageCard({ item, returnSerial }: { item: DocumotoManualNodeDto; returnSerial: string }) {
	const navigate = useNavigate();
	const serialQ = encodeURIComponent(returnSerial.trim());
	const to = `/admin/documoto-parts/page/${encodeURIComponent(String(item.entityId))}?serial=${serialQ}`;
	return (
		<button
			type="button"
			className="toc-card toc-card--page"
			onClick={() =>
				navigate(to, {
					state: {
						pageTitle: item.name,
						returnSerial: returnSerial.trim(),
					},
				})
			}
		>
			<div className="toc-card-visual">{item.rawImage ? <img src={item.rawImage} alt="" /> : null}</div>
			<div className="toc-card-meta">
				<div className="toc-card-name">{item.name}</div>
				<div className="toc-card-id">Page {String(item.entityId)}</div>
			</div>
		</button>
	);
}

function TocCardBand({ nodes, depth, returnSerial }: { nodes: DocumotoManualNodeDto[]; depth: number; returnSerial: string }) {
	if (nodes.length === 0 && depth === 0) {
		return <p className="toc-muted">No items to display.</p>;
	}
	return (
		<div className="toc-cards-band">
			{nodes.map((node) =>
				isDocumotoChapterNode(node.nodeType) ? (
					<ChapterCard key={String(node.entityId)} item={node} depth={depth} returnSerial={returnSerial} />
				) : (
					<PageCard key={String(node.entityId)} item={node} returnSerial={returnSerial} />
				),
			)}
		</div>
	);
}

export function DocumotoTocTree({ rootNodes, returnSerial }: Props) {
	return (
		<section className="toc-tree toc-tree--cards">
			<TocCardBand nodes={rootNodes} depth={0} returnSerial={returnSerial} />
		</section>
	);
}
